import { isWineCategory } from "./rules";
import type { MenuCategory, MenuDetail, MenuItem } from "./types";

/** A run of rows under an optional sub-header: a category's own items, then each sub-category's. */
export interface RowGroup {
  key: string;
  label: string | null;
  hidden: boolean;
  items: MenuItem[];
}

export interface CategoryBlock {
  category: MenuCategory;
  /** The category's own items in order: what its Riordina reorders and where its "Aggiungi" adds. */
  own: MenuItem[];
  /** Its sub-categories in order: what its Riordina reorders beside its own items. */
  children: MenuCategory[];
  groups: RowGroup[];
  /** Every row in display order, which the drawer's ‹ › walk. */
  ids: string[];
  count: number;
  isWine: boolean;
}

export interface MenuLayout {
  blocks: CategoryBlock[];
  /** Every item in display order, for the search. */
  ordered: MenuItem[];
  categoryName: Map<string, string>;
}

const byPosition = (a: { position: number }, b: { position: number }) => a.position - b.position;

/** The menu read for display: root categories in order, each with its own rows and one level of sub-categories. */
export function menuLayout(detail: Pick<MenuDetail, "categories" | "items"> | undefined): MenuLayout {
  const categories = [...(detail?.categories ?? [])].sort(byPosition);
  const byCategory = new Map<string, MenuItem[]>();
  for (const item of [...(detail?.items ?? [])].sort(byPosition)) {
    byCategory.set(item.categoryId, [...(byCategory.get(item.categoryId) ?? []), item]);
  }
  const blocks = categories
    .filter((c) => c.parentId === null)
    .map((category): CategoryBlock => {
      const own = byCategory.get(category.id) ?? [];
      const children = categories.filter((c) => c.parentId === category.id);
      const groups: RowGroup[] = [
        { key: category.id, label: null, hidden: false, items: own },
        ...children.map((child) => ({ key: child.id, label: child.name, hidden: child.hidden, items: byCategory.get(child.id) ?? [] })),
      ];
      const ids = groups.flatMap((g) => g.items.map((i) => i.id));
      return { category, own, children, groups, ids, count: ids.length, isWine: isWineCategory(category.isDrink, own) };
    });
  const index = new Map((detail?.items ?? []).map((i) => [i.id, i]));
  const ordered = blocks.flatMap((b) => b.ids.map((id) => index.get(id)).filter((i): i is MenuItem => i !== undefined));
  return { blocks, ordered, categoryName: new Map(categories.map((c) => [c.id, c.name])) };
}
